import { useSidebarSafe } from '@ophelos/ui/components/sidebar';
import { useMediaQuery } from '@ophelos/ui/hooks/use-media-query';
import { cn } from '@ophelos/ui/lib/utils';
import { useEffect, useRef } from 'react';
import type { AIChatOpenSource } from '@/contexts/AIChatContext';
import { useAIChat } from '@/contexts/AIChatContext';
import { ChatHeader } from './_shared/ChatHeader';
import { DefaultChatContent } from './agents/default/DefaultChatContent';
import { FinanceChatPanel } from './agents/finance/FinanceChatPanel';
import { SettingsHierarchyContent } from './agents/settings/SettingsHierarchyContent';

import { SupportChatContent } from './agents/support/SupportChatContent';

function AgentContent({ source }: { source: AIChatOpenSource }) {
  switch (source) {
    case 'finance':
      return <FinanceChatPanel />;
    case 'support':
      return <SupportChatContent />;
    case 'settings':
      return <SettingsHierarchyContent />;
    default:
      return <DefaultChatContent />;
  }
}

export function AIChatPanel() {
  const { isOpen, resolvedOpenSource } = useAIChat();
  const sidebar = useSidebarSafe();
  const sidebarWasOpenRef = useRef(false);
  // Tailwind `2xl` is 96rem; at that width both panels fit without collapsing the sidebar.
  const isBelow2xl = useMediaQuery('(max-width: 1535px)');

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally ignores sidebar — its ref is stable
  useEffect(() => {
    if (!sidebar) return;
    if (isOpen && isBelow2xl) {
      if (sidebar.open) {
        sidebarWasOpenRef.current = true;
        sidebar.setOpen(false);
      }
    } else if ((!isOpen || !isBelow2xl) && sidebarWasOpenRef.current) {
      sidebarWasOpenRef.current = false;
      sidebar.setOpen(true);
    }
  }, [isOpen, isBelow2xl]);

  return (
    <div
      className={cn(
        'shrink-0 h-screen max-h-screen transition-[width] duration-200 ease-linear overflow-hidden bg-sidebar-background',
        isOpen ? 'w-85 2xl:w-120' : 'w-0 '
      )}
      aria-hidden={!isOpen}
    >
      {isOpen && (
        <div className="flex flex-col h-full max-h-full w-85 2xl:w-120 pr-2 overflow-hidden">
          <div className="flex-1 flex flex-col h-full overflow-hidden min-h-0 pb-5 px-2 2xl:px-4">
            <ChatHeader />
            <AgentContent source={resolvedOpenSource} />
          </div>
        </div>
      )}
    </div>
  );
}
